import type { ApolloCache, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import type { MutationResult } from '../../graphql';
import type { Dbx } from '../account-store';
import { QUERY } from '../use-sub-users-account';

interface TData {
  substituteSubUserRestrictedLocationsForManyUSers: {
    location_id: string;
    permissions: {
      activity_log: boolean;
      content_managment: boolean;
      event_managment: boolean;
      view_and_download: boolean;
      customize_production_notes: boolean;
      id: string;
      subUser: {
        id: number;
        email: string;
      };
      location: {
        id: string;
        name: string;
        is_disabled: boolean;
      };
    }[];
  }[];
}

interface locationPermision {
  locationId: string;
  permissions: PerissionsData[];
}

interface PerissionsData {
  content_managment: boolean;
  event_managment: boolean;
  view_and_download: boolean;
  customize_production_notes: boolean;
  user_id: number;
  id?: number;
}

interface TDataGetUsers {
  getSubUsersForAccount: {
    result: Dbx.SubUserAccountsRecord[];
  };
}

interface TVariablesGetUsers {
  id: number;
}

interface TVariables {
  input: locationPermision[];
}

const MUTATION = gql`
  mutation SubstituteSubUserRestrictedLocationsForManyUSers(
    $input: [CreateSubUserRestrictedLocationsForManyUsers!]!
  ) {
    substituteSubUserRestrictedLocationsForManyUSers(input: $input) {
      location_id
      permissions {
        activity_log
        content_managment
        event_managment
        view_and_download
        customize_production_notes
        id
        subUser {
          id
          email
        }
        location {
          id
          name
          is_disabled
        }
      }
    }
  }
`;

interface Args {
  location_id: string;
  permissions: PerissionsData[];
}

export const useUpdateForManySubUserLocations = (): [
  ({ accountId, input }: { input: Args[]; accountId: number }) => Promise<void>,
  MutationResult<TVariables, 'locations'>
] => {
  const [mutation, result] = useMutation<TData, { input: Args[] }>(MUTATION);

  const [error, setError] = useState<Error | undefined>();

  return [
    useCallback(
      async ({ accountId, input }: { input: Args[]; accountId: number }): Promise<void> => {
        try {
          await mutation({
            variables: { input },
            update(cache: ApolloCache<unknown>, { data: fetchData }: FetchResult<TData>): void {
              const dataReadQuery = cache.readQuery<TDataGetUsers, TVariablesGetUsers>({
                query: QUERY,
                variables: {
                  id: accountId
                }
              });

              // Esto es para tener un solo arreglo de permisos de la respuesta
              const onlyPermission = fetchData?.substituteSubUserRestrictedLocationsForManyUSers.map((permisionWithLocationId) => permisionWithLocationId.permissions);
              const fetchPermisions = onlyPermission?.reduce((accumulator, permission) => [...accumulator, ...permission], []);

              if (dataReadQuery?.getSubUsersForAccount.result) {
                const handleDataReadQuery = dataReadQuery.getSubUsersForAccount.result.map(
                  (subuser) => {

                    const userPermisions = fetchPermisions?.filter((permissionUser) => permissionUser.subUser.id === subuser.id);
                    // Primero evalua si el usuario tiene locaciones ya que podría ser nuevo

                    if (!subuser.sub_user_locations) {
                      if(userPermisions) {
                        const newSubuser = {
                          ...subuser,
                          sub_user_locations: userPermisions
                        };

                        return newSubuser;
                      }

                      return subuser;
                    }

                    // Lista para editar
                    const newSubuserLocation = subuser.sub_user_locations.map((location) => {
                      // Si encuentra el permiso para editar

                      const fetchPermisionFound = userPermisions?.find(
                        (locationPermission) =>
                          Number(locationPermission.id) === Number(location?.id)
                      );

                      if (fetchPermisionFound) {
                        // Regresa el permiso editado al usuario

                        return fetchPermisionFound;
                      }

                      return location;
                    });

                    // Busca si hay permisos nuevos que no esten en el usuario previamente
                    const permisionsID = new Set(subuser.sub_user_locations.map((location) => location?.id));
                    const newUserPermisions = userPermisions?.filter((permissionUser) => !permisionsID.has(permissionUser.id));

                    if(newUserPermisions && newUserPermisions.length > 0) {

                      const newSubuser = {
                        ...subuser,
                        sub_user_locations: [...newSubuserLocation, ...newUserPermisions]
                      };

                      return newSubuser;
                    }

                    return { ...subuser, sub_user_locations: newSubuserLocation };

                  }
                );

                const reWriteQuery = {
                  getSubUsersForAccount: {
                    ...dataReadQuery.getSubUsersForAccount,
                    result: handleDataReadQuery
                  }
                };

                cache.writeQuery({
                  query: QUERY,
                  data: reWriteQuery
                });
              }
            }
          });
        } catch (error_: unknown) {
          setError(<Error>error_);
        }
      },
      [mutation]
    ),
    {
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
